import axios from 'axios';

// In production on Netlify, use relative URLs to go through Netlify functions
// In development, use localhost backend
// If REACT_APP_API_URL is explicitly set, use that (for custom deployments)
const API_BASE_URL = process.env.REACT_APP_API_URL || (
  process.env.NODE_ENV === 'production'
    ? '' // Empty string means relative URLs - requests go through Netlify functions
    : 'http://localhost:8000'
);

console.log('API Base URL:', API_BASE_URL);
console.log('Environment:', process.env.NODE_ENV);

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Store for pending request to refresh token
let refreshTokenPromise = null;

// Request interceptor - add JWT token to all requests
api.interceptors.request.use(
  (config) => {
    console.log(`Making ${config.method?.toUpperCase()} request to ${config.url}`);

    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const sessionId = localStorage.getItem('session_id');
    if (sessionId) {
      config.headers['X-Session-Id'] = sessionId;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh on 401
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Skip refresh logic for auth endpoints to avoid infinite loops
    const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/');

    // If we get a 401 and haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        // If a refresh is already in progress, wait for it
        if (refreshTokenPromise) {
          try {
            await refreshTokenPromise;
          } catch (waitError) {
            // The refresh failed while we were waiting, don't retry
            return Promise.reject(error);
          }
          // Retry original request with new token
          const token = localStorage.getItem('access_token');
          if (!token) {
            return Promise.reject(error);
          }
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }

        // Start token refresh
        refreshTokenPromise = refreshAccessToken();
        try {
          await refreshTokenPromise;
        } finally {
          refreshTokenPromise = null;
        }

        // Retry original request with new token
        const token = localStorage.getItem('access_token');
        if (!token) {
          return Promise.reject(error);
        }
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        refreshTokenPromise = null;
        // Refresh failed - logout user
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_id');
        localStorage.removeItem('duckpilot_mode');
        // Only redirect if we're not already on the landing page
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
        return Promise.reject(refreshError);
      }
    }

    console.error('API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// Function to refresh access token
async function refreshAccessToken() {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
    refresh_token: refreshToken,
  });

  const { access_token, refresh_token } = response.data;
  localStorage.setItem('access_token', access_token);
  localStorage.setItem('refresh_token', refresh_token);

  return access_token;
}

export default api; 
